import React, { useEffect, useState, useRef } from 'react';
import { OverlayView } from '@react-google-maps/api';
import { DigitalMapBusRoute } from 'models/digitalMap';

const getLatLng = (latLng: { lat: number; lng: number }) => {
  return new google.maps.LatLng(latLng.lat, latLng.lng);
};

interface BusState {
  pathIndex: number;
  currentPosition: google.maps.LatLng;
  offset: number; // Offset in milliseconds to stagger bus positions
}

interface Props {
  route: DigitalMapBusRoute;
}

export const SimulatedBusOverlay: React.FC<Props> = ({ route }) => {
  const [buses, setBuses] = useState<BusState[]>([]);
  const busesRef = useRef<BusState[]>([]);

  const interpolatePosition = (
    start: google.maps.LatLng,
    end: google.maps.LatLng,
    fraction: number
  ): google.maps.LatLng => {
    const lat = start.lat() + (end.lat() - start.lat()) * fraction;
    const lng = start.lng() + (end.lng() - start.lng()) * fraction;
    return new google.maps.LatLng(lat, lng);
  };

  // Initialize buses with staggered offsets
  useEffect(() => {
    if (route.route.length < 2) return;

    const busCount = route.simulated_bus_count || 1;
    const duration = 1000; // 1 second between points
    const totalDuration = duration * route.route.length;

    const initialBuses = Array.from({ length: busCount }, (_, index) => {
      // Distribute buses evenly along the route by offsetting their start times
      const offset = (totalDuration * index) / busCount;
      const initialPathIndex = Math.floor(offset / duration);
      const initialFraction = (offset % duration) / duration;

      const start = getLatLng(route.route[initialPathIndex]);
      const end = getLatLng(route.route[(initialPathIndex + 1) % route.route.length]);
      const initialPosition = interpolatePosition(start, end, initialFraction);

      return {
        pathIndex: initialPathIndex,
        currentPosition: initialPosition,
        offset,
      };
    });

    setBuses(initialBuses);
    busesRef.current = initialBuses;
  }, [route]);

  // Animate buses
  useEffect(() => {
    if (route.route.length < 2 || buses.length === 0) return;

    let animationFrame: number;
    let startTime: number;
    const duration = 1000; // 1 second between points

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;

      const updatedBuses = busesRef.current.map((bus) => {
        const adjustedElapsed = (elapsed + bus.offset) % (duration * route.route.length);
        const currentIdx = Math.floor(adjustedElapsed / duration);
        const nextIdx = (currentIdx + 1) % route.route.length;
        const fraction = (adjustedElapsed % duration) / duration;

        const interpolated = interpolatePosition(
          getLatLng(route.route[currentIdx]),
          getLatLng(route.route[nextIdx]),
          fraction
        );

        return {
          ...bus,
          pathIndex: currentIdx,
          currentPosition: interpolated,
        };
      });

      busesRef.current = updatedBuses;
      setBuses(updatedBuses);
      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [route, buses.length]); // Removed buses from dependencies

  return (
    <>
      {buses.map((bus, index) => (
        <OverlayView
          key={`${route.id}-${index}`}
          position={bus.currentPosition}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <div
            style={{
              position: 'absolute',
              transform: 'translate(-50%, -50%)',
              backgroundColor: '#fff',
              borderRadius: '50%',
              width: '58px',
              height: '58px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img src="/static/images/map/bus.svg" />
          </div>
        </OverlayView>
      ))}
    </>
  );
};
